// features/workstations/dashboard-sections/strategy-health.js
// ═══════════════════════════════════════════════════════════
// STRATEGY HEALTH SCORECARD — is the strategy still working?
// ═══════════════════════════════════════════════════════════
// Tier B meta. Pure-analytics widget. Rolls up five dimensions
// of edge health into green / yellow / red tiles with a
// composite overall grade at the top.
//
// Metrics:
//   WIN RATE             wins / (wins + losses)
//   EXPECTANCY (R)       avg R per trade
//   RULE COMPLIANCE      % of trades with zero rule_breaks
//   MAX DRAWDOWN         peak-to-trough cumulative P&L in $
//   MAX CONSEC LOSSES    longest loss streak in window
//
// Each tile shows: current value · delta vs prior window ·
// hint copy · sample size. Tiles with insufficient data render
// in a dim ‘unknown’ state.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PractitionerLens,
  MetaField,
} from '../../../components/widgets/primitives';

const GRADE_LABELS = {
  green:   'healthy',
  yellow:  'watch',
  red:     'action',
  unknown: 'unknown',
};

const OVERALL_HEADLINES = {
  green:   'Strategy is running well.',
  yellow:  'Drift detected — pay attention.',
  red:     'Action needed — review the red tiles.',
  unknown: 'Not enough data yet.',
};

const OVERALL_SUMMARIES = {
  green:   'Five dimensions checked; each is inside normal bands for the window. Keep the cadence.',
  yellow:  'Some dimensions are drifting. Not broken yet — but the signals compound if ignored.',
  red:     'One or more dimensions crossed an edge band. Review the metric hints and consider pausing until the cause is clear.',
  unknown: 'Log at least 5–10 closed trades in the window and the scorecard will populate.',
};

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function StrategyHealthSection({ workstation, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowDays, setWindowDays] = useState(30);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
        days: String(windowDays),
      });
      const res = await fetch(`/api/workstations/strategy-health?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setData(payload || null);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId, windowDays]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <section className="ws-health">
        <div className="ws-health-loading">Computing strategy health…</div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="ws-health">
        <Eyebrow>strategy health — read failed</Eyebrow>
        <SerifSummary>{error || 'No data returned.'}</SerifSummary>
      </section>
    );
  }

  return (
    <ScorecardBody
      data={data}
      windowDays={windowDays}
      onWindowChange={setWindowDays}
    />
  );
}

// ═══════════════════════════════════════════════════════════
// SCORECARD BODY
// ═══════════════════════════════════════════════════════════
function ScorecardBody({ data, windowDays, onWindowChange }) {
  const overall = data.overall_grade || 'unknown';
  const metrics = Array.isArray(data.metrics) ? data.metrics : [];

  return (
    <section className="ws-health">
      <Eyebrow parts={[
        'strategy health',
        `${data.trade_count} trades in ${data.window_days}d`,
        overall !== 'unknown' ? GRADE_LABELS[overall] : 'unknown',
      ]} />

      <SerifHeadline>
        <span className={`ws-health-tone-${overall}`}>{OVERALL_HEADLINES[overall]}</span>
      </SerifHeadline>
      <SerifSummary>{OVERALL_SUMMARIES[overall]}</SerifSummary>

      {/* Window selector */}
      <div className="ws-health-window-row">
        <span className="ws-health-window-label">window</span>
        {[7, 14, 30, 60, 90].map((d) => (
          <button
            key={d}
            type="button"
            className={`ws-health-window-chip ${d === windowDays ? 'is-active' : ''}`}
            onClick={() => onWindowChange(d)}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Metric tiles */}
      <SectionStrip label="metrics" count={metrics.length}>
        <div className="ws-health-grid">
          {metrics.map((m) => (
            <MetricTile key={m.key} m={m} />
          ))}
        </div>
      </SectionStrip>

      <PractitionerLens label="how to read this">
        Overall grade is the worst of the five with enough data — one
        red metric pulls the whole scorecard red. A yellow after a
        stretch of green is the earliest drift signal; don’t wait for
        it to go red to adjust.
      </PractitionerLens>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// METRIC TILE
// ═══════════════════════════════════════════════════════════
function MetricTile({ m }) {
  const grade = m.grade || 'unknown';

  // Format the big value + trend
  const { display, priorDisplay, deltaDisplay, deltaTone } = formatMetric(m);

  if (m.too_few) {
    return (
      <div className={`ws-health-tile ws-health-tile-unknown`}>
        <div className="ws-health-tile-head">
          <span className="ws-health-tile-label">{m.label}</span>
          <span className="ws-health-tile-grade ws-health-tone-unknown">—</span>
        </div>
        <div className="ws-health-tile-value ws-health-tone-unknown">—</div>
        <div className="ws-health-tile-hint">{m.reason || 'Not enough data.'}</div>
      </div>
    );
  }

  return (
    <div className={`ws-health-tile ws-health-tile-${grade}`}>
      <div className="ws-health-tile-head">
        <span className="ws-health-tile-label">{m.label}</span>
        <span className={`ws-health-tile-grade ws-health-tone-${grade}`}>{GRADE_LABELS[grade]}</span>
      </div>
      <div className={`ws-health-tile-value ws-health-tone-${grade}`}>{display}</div>
      <div className="ws-health-tile-meta">
        {priorDisplay && (
          <span className="ws-health-tile-prior">prior {priorDisplay}</span>
        )}
        {deltaDisplay && (
          <span className={`ws-health-tile-delta ws-health-tone-${deltaTone}`}>{deltaDisplay}</span>
        )}
      </div>
      {m.hint && <div className="ws-health-tile-hint">{m.hint}</div>}
      {m.sample && (
        <MetaField label="sample">
          {m.sample.current} (prior {m.sample.prior ?? 0})
        </MetaField>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// FORMATTERS per metric
// ═══════════════════════════════════════════════════════════
function formatMetric(m) {
  switch (m.key) {
    case 'win_rate': return {
      display: m.value != null ? `${Math.round(m.value * 100)}%` : '—',
      priorDisplay: m.prior != null ? `${Math.round(m.prior * 100)}%` : null,
      deltaDisplay: m.delta != null
        ? `${m.delta >= 0 ? '+' : ''}${Math.round(m.delta * 100)}pp`
        : null,
      deltaTone: deltaTone(m.delta),
    };
    case 'expectancy_r': return {
      display: m.value != null ? `${m.value >= 0 ? '+' : ''}${m.value.toFixed(2)}R` : '—',
      priorDisplay: m.prior != null ? `${m.prior >= 0 ? '+' : ''}${m.prior.toFixed(2)}R` : null,
      deltaDisplay: m.delta != null
        ? `${m.delta >= 0 ? '+' : ''}${m.delta.toFixed(2)}R`
        : null,
      deltaTone: deltaTone(m.delta),
    };
    case 'rule_compliance': return {
      display: m.value != null ? `${Math.round(m.value * 100)}%` : '—',
      priorDisplay: m.prior != null ? `${Math.round(m.prior * 100)}%` : null,
      deltaDisplay: m.delta != null
        ? `${m.delta >= 0 ? '+' : ''}${Math.round(m.delta * 100)}pp`
        : null,
      deltaTone: deltaTone(m.delta),
    };
    case 'max_drawdown_usd': return {
      display: m.value != null ? fmtUsd(-m.value) : '—',  // render as loss
      priorDisplay: m.prior != null ? fmtUsd(-m.prior) : null,
      deltaDisplay: m.delta != null
        ? `${m.delta >= 0 ? 'deeper' : 'shallower'} ${fmtUsd(Math.abs(m.delta)).replace('−', '').replace('+', '')}`
        : null,
      // Deeper drawdown = worse = terra delta
      deltaTone: m.delta == null ? 'steel'
        : m.delta > 0 ? 'terra'
        : m.delta < 0 ? 'sage' : 'steel',
    };
    case 'max_consec_losses': return {
      display: m.value != null ? `${m.value}` : '—',
      priorDisplay: m.prior != null ? `${m.prior}` : null,
      deltaDisplay: m.delta != null && m.delta !== 0
        ? `${m.delta > 0 ? '+' : ''}${m.delta}`
        : null,
      // Longer streak = worse = terra delta
      deltaTone: m.delta == null ? 'steel'
        : m.delta > 0 ? 'terra'
        : m.delta < 0 ? 'sage' : 'steel',
    };
    default: return {
      display: String(m.value ?? '—'),
      priorDisplay: null,
      deltaDisplay: null,
      deltaTone: 'steel',
    };
  }
}

function deltaTone(d) {
  if (d == null || d === 0) return 'steel';
  return d > 0 ? 'sage' : 'terra';
}

function fmtUsd(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  const abs = Math.abs(n);
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}
